/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Modal,
} from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "../firebase.config";
import { collection, addDoc, doc, deleteDoc } from "firebase/firestore";

const functions = getFunctions(app);
import { subscribeToBookings } from "../db_calls_wrapper";
import { useCustomer, useRoute, useSelectedDate, useSettings, useAuthStore, useProducts, useStripeTestMode } from "../stores";
import uploadProcessor from "../uploadProcessor";
import { getBookingsFromCache } from "../services/bookingsCache";
import { getCombinationFromCache } from "../services/locksCache";
import {
  registerForNotifications,
  disableNotifications,
  areNotificationsEnabled,
  isPushSupported,
  isStandalone,
} from "../services/fcmService";
import { getTenantStoreContext } from "../services/userStoreCache";
import {
  formatPhoneNumberProgressive as formatPhoneNumber,
  parseFirestoreTimestamp,
  formatPhoneNumber as formatPhoneNumberCalendar,
  capitalizeWords,
  formatAddressLines,
  getTZDayKey,
} from "../utils/formatting";
import AuthHeader from "../components/AuthHeader";
import ContactOptionsModal from "../utils/ContactOptionsModal";

const auth = getAuth(app);

const Base = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [shopInfo, setShopInfo] = useState({
    address: "",
    phone: "",
    email: "",
  });
  const [storeLogo, setStoreLogo] = useState("");
  const [storeName, setStoreName] = useState("");
  const [startingAddress, setStartingAddress] = useState("");
  
  // Get selected date from navigation state (if coming from Calendar)
  const selectedDateFromCalendar = location.state?.selectedDate;

  // Selected date store
  const getBaseDate = useSelectedDate((state) => state.getBaseDate);
  const getBaseDateTimestamp = useSelectedDate((state) => state.getBaseDateTimestamp);
  const setBaseDate = useSelectedDate((state) => state.setBaseDate);
  const getTodayProductsListVisible = useSelectedDate((state) => state.getTodayProductsListVisible);
  const getTodayProductsListTimestamp = useSelectedDate((state) => state.getTodayProductsListTimestamp);
  const setTodayProductsListVisible = useSelectedDate((state) => state.setTodayProductsListVisible);
  const TIMESTAMP_FRESHNESS_MS = 120000; // 120 seconds

  // Current display date state (for navigation) - initialize from Zustand or calendar
  const [currentDisplayDate, setCurrentDisplayDate] = useState(() => {
    if (selectedDateFromCalendar) {
      const date = new Date(selectedDateFromCalendar.getTime());
      date.setHours(0, 0, 0, 0);
      setBaseDate(date); // Store in Zustand
      return date;
    }

    const savedAt = getBaseDateTimestamp?.();
    const now = Date.now();
    const isFreshSelection =
      typeof savedAt === "number" && now - savedAt <= TIMESTAMP_FRESHNESS_MS;

    // If stale (or missing), reset to today's bookings.
    if (!isFreshSelection) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setBaseDate(today);
      return today;
    }

    // Keep recently selected date (within last 120 seconds).
    return getBaseDate();
  });

  // Update Zustand when date changes
  useEffect(() => {
    setBaseDate(currentDisplayDate);
  }, [currentDisplayDate, setBaseDate]);

  // Ref to track the current calculation's dayKey to prevent race conditions
  const currentCalculationRef = React.useRef(null);

  // Rentals state
  const [todaysRentals, setTodaysRentals] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showProductsList, setShowProductsList] = useState(() => {
    const savedAt = getTodayProductsListTimestamp?.();
    const now = Date.now();
    const isFreshSelection =
      typeof savedAt === "number" && now - savedAt <= TIMESTAMP_FRESHNESS_MS;

    if (!isFreshSelection) {
      setTodayProductsListVisible(true);
      return true;
    }

    return getTodayProductsListVisible?.() ?? true;
  });
  const [printingProducts, setPrintingProducts] = useState(false);
  // Hide Print Products button while printing service is down (set to true when back up)
  const PRINT_PRODUCTS_BUTTON_VISIBLE = false;

  useEffect(() => {
    setTodayProductsListVisible(showProductsList);
  }, [showProductsList, setTodayProductsListVisible]);

  // Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsMuted, setNotificationsMuted] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  // Route planning state
  const [selectedRentals, setSelectedRentals] = useState([]); // Array of selected rental IDs

  // Upload status tracking
  const [uploadsInProgress, setUploadsInProgress] = useState(0);
  const [uploadsCompleted, setUploadsCompleted] = useState(0);

  // Payment collection state
  const [collectingPayment, setCollectingPayment] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Zustand store
  const currentCustomer = useCustomer((state) => state.customer);
  const setCustomer = useCustomer((state) => state.setCustomer);
  const getCachedRoute = useRoute((state) => state.getRoute);
  const setCachedRoute = useRoute((state) => state.setRoute);
  const isRouteValid = useRoute((state) => state.isRouteValid);


  // Helper function to format date as "Mon, Jan 1"
  const formatDateShort = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${dayNames[d.getDay()]}, ${monthNames[d.getMonth()]} ${d.getDate()}`;
  };

  const formatDateLong = (date) => {
    if (!date) return "N/A";

    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date.seconds) {
      dateObj = new Date(date.seconds * 1000);
    } else {
      dateObj = new Date(date);
    }

    return dateObj.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Helper function to determine card type and styling for a booking
  const getBookingCardInfo = (booking, targetDate) => {
    const dateKey = getTZDayKey(targetDate.getTime());
    const startMillis = parseFirestoreTimestamp(booking.startDate);
    const endMillis = parseFirestoreTimestamp(booking.endDate);
    const startKey = getTZDayKey(startMillis);
    const endKey = getTZDayKey(endMillis);

    const hasDelivery = booking.delivery || false;
    const hasHomePickup = booking.homePickup || false;
    const isInStore = booking.inStorePickup || (!hasDelivery && !hasHomePickup);

    const startMatchesDate = startKey === dateKey;
    const endMatchesDate = endKey === dateKey;

    // 1. Delivery + Start Date -> Red Border + "DELIVERY"
    if (hasDelivery && startMatchesDate) {
      return { type: "DELIVERY", color: "#C4453D", borderColor: "#C4453D" };
    }

    // 2. HomePickup + End Date -> Green Border + "HOME PICKUP"
    if (hasHomePickup && endMatchesDate) {
      return { type: "HOME PICKUP", color: "#1F8A5B", borderColor: "#1F8A5B" };
    }

    // 3. InStorePickup + Start Date -> Yellow Border + "IN-STORE PICKUP"
    if (isInStore && startMatchesDate) {
      return { type: "IN-STORE PICKUP", color: "#B4761B", borderColor: "#B4761B" };
    }

    // 4. InStorePickup + End Date -> Gray Border + "IN-STORE DROP-OFF"
    if (isInStore && endMatchesDate) {
      return { type: "IN-STORE DROP-OFF", color: "#8A9794", borderColor: "#8A9794" };
    }

    // Default fallback
    return { type: "RENTAL", color: "#8A9794", borderColor: "#8A9794" };
  };

  // Check notification status
  const checkNotificationStatus = () => {
    const enabled = areNotificationsEnabled();
    setNotificationsEnabled(enabled);

    // Check if muted (stored in localStorage)
    const muted = localStorage.getItem("notificationsMuted") === "true";
    setNotificationsMuted(muted);

    console.log("🔔 Notification status:", { enabled, muted });
  };

  // Handle notification button click
  const handleNotificationToggle = async () => {
    if (!user) return;

    // Case 1: Not signed up for notifications
    if (!notificationsEnabled) {
      console.log("🔔 User requesting notification signup");
      try {
        // On iPhone, push only exists inside a Home Screen web app. In a plain
        // Safari tab the APIs are absent entirely, so say what to actually do
        // rather than sending them to hunt through browser settings.
        if (!(await isPushSupported())) {
          alert(
            !isStandalone()
              ? "To get notifications on iPhone, open the Share menu and choose \"Add to Home Screen\", then open the app from that icon and tap the bell again."
              : "❌ This browser can't receive push notifications."
          );
          return;
        }

        const success = await registerForNotifications();
        if (success) {
          setNotificationsEnabled(true);
          setNotificationsMuted(false);
          localStorage.setItem("notificationsMuted", "false");
          alert(
            "✅ Notifications enabled! Replies to texts you send will come to this phone."
          );
        } else {
          alert(
            Notification.permission === "denied"
              ? "❌ Notifications are blocked for this app. Re-enable them in your phone's Settings, then tap the bell again."
              : "❌ Failed to enable notifications."
          );
        }
      } catch (error) {
        console.error("❌ Error enabling notifications:", error);
        alert("❌ Failed to enable notifications: " + error.message);
      }
      return;
    }

    // Case 2 & 3: Toggle mute status
    const newMutedState = !notificationsMuted;
    setNotificationsMuted(newMutedState);
    localStorage.setItem("notificationsMuted", newMutedState.toString());

    console.log(`🔔 Notifications ${newMutedState ? "muted" : "unmuted"}`);

    if (newMutedState) {
      // Muted
      console.log("🔕 Notifications muted for this device");
    } else {
      // Unmuted
      console.log("🔔 Notifications unmuted for this device");
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  // Get products from Zustand store (for printing)
  const products1 = useProducts((state) => state.products1);
  const products2 = useProducts((state) => state.products2);
  
  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const address = settings.startingAddress?.address || "";
      const phone = settings.storePhone?.phone || "";
      const email = settings.storeEmail?.email || "";
      const logo = settings.storeLogo?.url || "";
      const storeDisplayName = settings.storeName?.name || "";
      
      setStartingAddress(address);
      setShopInfo({ address, phone, email });
      setStoreLogo(logo);
      setStoreName(storeDisplayName);
    }
  }, [settings]);

  // Cleanup print documents helper
  const cleanupPrintDocuments = async (collectionPath, documentIds) => {
    try {
      const deletePromises = documentIds.map(id => deleteDoc(doc(db, collectionPath, id)));
      await Promise.all(deletePromises);
      console.log(`🧹 Cleaned up ${documentIds.length} print documents`);
    } catch (error) {
      console.error("❌ Error cleaning up print documents:", error);
    }
  };

  // Print function
  const handlePrint = async () => {
    if (!currentDisplayDate) {
      alert("Please select a date first");
      return;
    }

    try {
      const { tenantId, storeId } = getTenantStoreContext();
      if (!tenantId || !storeId) {
        alert("Error: Tenant/Store context not available");
        return;
      }

      // Get bookings for selected date
      const dateKey = getTZDayKey(currentDisplayDate.getTime());
      
      const bookingsToPrint = todaysRentals.filter((booking) => {
        const startMillis = parseFirestoreTimestamp(booking.startDate);
        if (!startMillis) return false;
        const startKey = getTZDayKey(startMillis);
        
        // Only bookings where start date matches selected date
        if (startKey !== dateKey) return false;
        
        // Filter: Delivery (start date) OR In-store Pickup (start date)
        const hasDelivery = booking.delivery || false;
        const hasInStorePickup = booking.inStorePickup || false;
        
        return hasDelivery || hasInStorePickup;
      });

      if (bookingsToPrint.length === 0) {
        alert("No deliveries or in-store pickups found for this date");
        return;
      }

      // Get product category names from settings
      const product1Name = settings.product1Title?.title || "Product1";
      const product2Name = settings.product2Title?.title || "Product2";

      // Format date string
      const dateString = formatDateShort(currentDisplayDate);

      // Create printing collection path
      const printingPath = `tenants/${tenantId}/stores/${storeId}/printing`;
      const printingRef = collection(db, printingPath);

      const documentIds = [];

      // Process each booking
      for (const booking of bookingsToPrint) {
        // Format product1 array (exclude items with physicalItem = false)
        const product1Array = [];
        if (Array.isArray(booking.product1)) {
          booking.product1.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products1.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              product1Array.push(`(${item.quantity}x) ${productName}`);
            }
          });
        }

        // Format product2 array (exclude items with physicalItem = false)
        const product2Array = [];
        if (Array.isArray(booking.product2)) {
          booking.product2.forEach((item) => {
            if (item && item.quantity > 0) {
              // Find product in store to check physicalItem
              const product = products2.find(p => p.id === item.id || p.id === item.productId);
              
              // Skip if physicalItem is false
              if (product && product.physicalItem === false) {
                return;
              }
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              product2Array.push(`(${item.quantity}x) ${productName}`);
            }
          });
        }

        const rawPhone = booking.customerPhone || booking.phone || "";
        const formattedPhone = formatPhoneNumber(rawPhone);

        const documentData = {
          firstName: capitalizeWords(booking.firstName || ""),
          lastName: capitalizeWords(booking.lastName || ""),
          phoneNumber: formattedPhone,
          dateString: dateString,
          product1: product1Array,
          product2: product2Array,
          product1Name: product1Name,
          product2Name: product2Name,
          delivery: booking.delivery || false,
          inStorePickup: booking.inStorePickup || false,
          adminNotes: booking.adminNotes || "",
        };

        const docRef = await addDoc(printingRef, documentData);
        documentIds.push(docRef.id);
        console.log(`✅ Created print document ${docRef.id} for booking ${booking.id || booking.bookingId}`);
      }

      // Schedule cleanup
      setTimeout(() => {
        cleanupPrintDocuments(printingPath, documentIds);
      }, 15000);
      
    } catch (error) {
      console.error("❌ Error printing:", error);
      alert(`Failed to print: ${error.message}`);
    }
  };

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
        navigate("/login");
    } else {
        setLoading(false);
        checkNotificationStatus();
    }
  }, [isAuthenticated, navigate]);

  // Load bookings from cache on mount (works offline)
  useEffect(() => {
    console.log("📦 Loading bookings from cache...");
    const cachedBookings = getBookingsFromCache();
    
    if (cachedBookings.length > 0) {
      console.log(`✅ Loaded ${cachedBookings.length} booking(s) from cache`);
      setTodaysRentals(cachedBookings);
    }
  }, []);

  // Subscribe to bookings for real-time updates (when online)
  useEffect(() => {
    console.log("Setting up Firestore bookings subscription...");

    const unsubscribeBookings = subscribeToBookings((bookings) => {
      console.log("📦 Bookings updated from Firestore:", bookings.length);

      // Log each booking with full details
      bookings.forEach((booking, index) => {
        console.log(`\n🎫 ====== BOOKING ${index + 1} ======`);
        console.log("📋 Customer:", {
          name: `${booking.firstName || ""} ${booking.lastName || ""}`.trim(),
          phone: booking.customerPhone || "N/A",
          email: booking.customerEmail || "N/A",
          address: booking.deliveryAddress || "N/A",
        });
        console.log("📅 Booking:", {
          bookingId: booking.bookingId || booking.id || "N/A",
          startDate: booking.startDate
            ? new Date(booking.startDate.seconds * 1000).toLocaleString()
            : "N/A",
          endDate: booking.endDate
            ? new Date(booking.endDate.seconds * 1000).toLocaleString()
            : "N/A",
          days: booking.days || "N/A",
          status: booking.status || "N/A",
        });
        const product1Items = booking.product1;
        const product2Items = booking.product2;
        
        console.log("🚴 Bicycles:", {
          product1: product1Items || [],
          totalBikes:
            product1Items?.reduce((sum, bike) => sum + bike.quantity, 0) || 0,
        });
        // Format add-ons with helmet sizes on separate lines
        if (product2Items && product2Items.length > 0) {
          console.log("🎁 Add-ons:");
          const helmets = product2Items.filter((addon) =>
            addon.name.toLowerCase().includes("helmet")
          );
          const otherAddOns = product2Items.filter(
            (addon) => !addon.name.toLowerCase().includes("helmet")
          );

          // Show helmets grouped
          if (helmets.length > 0) {
            console.log("  Helmet:");
            helmets.forEach((helmet) => {
              // Extract size from name (e.g., "Helmet - Large/XL" -> "Large/XL")
              const sizeMatch = helmet.name.match(/Helmet\s*-\s*(.+)/i);
              const size = sizeMatch ? sizeMatch[1] : helmet.name;
              console.log(`    (${helmet.quantity}) ${size}`);
            });
          }

          // Show other add-ons normally
          otherAddOns.forEach((addon) => {
            console.log(`  (${addon.quantity}) ${addon.name}`);
          });
        } else {
          console.log("🎁 Add-ons: None");
        }
        console.log("💰 Payment:", {
          totalCharged: booking.payments?.[0]?.costBreakdown?.totalCharged || "N/A",
          paymentStatus: booking.status || "N/A",
        });
        console.log("📝 Additional:", {
          notes: booking.notes || "",
        });
        console.log("⏰ Timestamps:", {
          createdAt: booking.createdAt
            ? new Date(booking.createdAt.seconds * 1000).toLocaleString()
            : "N/A",
          updatedAt: booking.updatedAt
            ? new Date(booking.updatedAt.seconds * 1000).toLocaleString()
            : "N/A",
        });
        console.log("🔍 Raw Data:", booking);
        console.log("============================\n");
      });

      setTodaysRentals(bookings);
    });

    return () => {
      console.log("Unsubscribing from bookings collection");
      unsubscribeBookings();
    };
  }, []);

  // Listen to upload processor events for status indicator
  useEffect(() => {
    const handleUploadEvent = (event) => {
      if (event.type === "upload_start") {
        setUploadsInProgress((prev) => prev + 1);
      } else if (event.type === "upload_success") {
        setUploadsInProgress((prev) => Math.max(0, prev - 1));
        setUploadsCompleted((prev) => prev + 1);

        // Clear completed count after 2 seconds
        setTimeout(() => {
          setUploadsCompleted((prev) => Math.max(0, prev - 1));
        }, 2000);
      } else if (event.type === "upload_failed") {
        setUploadsInProgress((prev) => Math.max(0, prev - 1));
      }
    };

    uploadProcessor.addListener(handleUploadEvent);

    return () => {
      uploadProcessor.removeListener(handleUploadEvent);
    };
  }, []);

  // Monitor current customer changes
  useEffect(() => {
    if (currentCustomer) {
      console.log(
        "🎯 Current customer in Zustand store updated:",
        currentCustomer
      );
      console.log("   Name:", currentCustomer.fullName);
      console.log("   Phone:", currentCustomer.phone);
      console.log("   ID:", currentCustomer.id);
    } else {
      console.log("🎯 Current customer in Zustand store: null");
    }
  }, [currentCustomer]);

  const toggleRentalSelection = (rentalId) => {
    setSelectedRentals((prev) => {
      if (prev.includes(rentalId)) {
        // Unselect
        return prev.filter((id) => id !== rentalId);
      } else {
        // Select
        return [...prev, rentalId];
      }
    });
  };

  // Helper: Generate hash from bookings for route cache validation
  const generateBookingsHash = (bookings, startAddr) => {
    if (!bookings || bookings.length === 0) return "";
    // Create a hash from booking IDs and addresses
    const bookingsStr = bookings
      .map((b) => `${b.id || b.bookingId}:${b.deliveryAddress || ""}`)
      .sort()
      .join("|");
    
    // Add starting address to hash to ensure routes from different start points are cached separately
    const hashString = `${bookingsStr}|${startAddr || "GPS"}`;

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < hashString.length; i++) {
      const char = hashString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  };

  // Payment handlers
  const handleCollectPayment = async (booking) => {
    const bookingId = booking.id || booking.bookingId;
    if (!booking || !bookingId) {
      alert("Error: Booking information not available");
      return;
    }

    const totalAmountCents = booking.totalAmount || 0;
    if (totalAmountCents <= 0) {
      alert("Error: Invalid payment amount");
      return;
    }

    setCollectingPayment(true);

    try {
      const createPaymentLink = httpsCallable(functions, "createPaymentLink");
      const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";
      
      const paymentResult = await createPaymentLink({
        amountCents: totalAmountCents,
        description: `Payment for Booking ${bookingId}`,
        customerEmail: booking.customerEmail || "",
        customerPhone: booking.customerPhone || "",
        customerName: customerName,
        saveCard: true,
        bookingId: bookingId,
        isBookingEdit: true,
        skipBookingConfirmation: true,
        testMode: useTestStripe,
      });

      if (!paymentResult.data?.url) {
        throw new Error("Failed to create payment link");
      }

      navigate("/stripe-checkout", {
        state: {
          paymentUrl: paymentResult.data.url,
          bookingId: bookingId,
          referrer: "/today",
        },
      });
    } catch (error) {
      console.error("❌ Error creating payment link:", error);
      setCollectingPayment(false);
      alert("Error: " + (error.message || "Failed to create payment link"));
    }
  };

  const handleSendTextInvoice = async (booking) => {
    const bookingId = booking.id || booking.bookingId;
    if (!booking || !bookingId) {
      alert("Error: Booking information not available");
      return;
    }

    const totalAmountCents = booking.totalAmount || 0;
    if (totalAmountCents <= 0) {
      alert("Error: Invalid payment amount");
      return;
    }

    if (!booking.customerPhone) {
      alert("Error: Customer phone number not available");
      return;
    }

    setSendingInvoice(true);

    try {
      const createPaymentLink = httpsCallable(functions, "createPaymentLink");
      const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";
      
      const paymentResult = await createPaymentLink({
        amountCents: totalAmountCents,
        description: `Payment for Booking ${bookingId}`,
        customerEmail: booking.customerEmail || "",
        customerPhone: booking.customerPhone || "",
        customerName: customerName,
        saveCard: true,
        bookingId: bookingId,
        isBookingEdit: true,
        skipBookingConfirmation: true,
        testMode: useTestStripe,
      });

      if (!paymentResult.data?.url) {
        throw new Error("Failed to create payment link");
      }

      const sendPaymentLinkSms = httpsCallable(functions, "sendPaymentLinkSms");
      await sendPaymentLinkSms({
        bookingId: bookingId,
        paymentUrl: paymentResult.data.url,
        customerPhone: booking.customerPhone,
        customerName: customerName,
      });

      alert("Payment link sent successfully!");
    } catch (error) {
      console.error("❌ Error sending payment link:", error);
      alert("Error: " + (error.message || "Failed to send payment link"));
    } finally {
      setSendingInvoice(false);
    }
  };

  // Background route calculation when Base page loads
  useEffect(() => {
    const calculateRouteInBackground = async () => {
      const filteredRentals = getFilteredRentals();
      if (filteredRentals.length === 0) return;

      // Get day key for current display date
      const dayKey = getTZDayKey(currentDisplayDate.getTime());
      
      // Set this as the current calculation to prevent race conditions
      currentCalculationRef.current = dayKey;
      
      // Map rentals to ensure consistency (filter and add fullName)
      const routeRentalsForHash = filteredRentals
        .filter((r) => {
          const hasDelivery = r.delivery || false;
          const hasHomePickup = r.homePickup || false;
          return (hasDelivery || hasHomePickup) && r.deliveryAddress;
        })
        .map((r) => ({
          ...r,
          fullName: r.fullName || `${r.firstName || ""} ${r.lastName || ""}`.trim() || "Unknown",
          customerPhone: r.customerPhone || r.phone,
        }));
      
      if (routeRentalsForHash.length === 0) {
        return; // No valid rentals for route
      }
      
      // Generate bookings hash
      const bookingsHash = generateBookingsHash(routeRentalsForHash, startingAddress);
      
      // Check if we have a valid cached route
      if (isRouteValid(dayKey, bookingsHash)) {
        console.log("✅ Using cached route for", dayKey);
        return;
      }

      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps) {
        console.log("⏳ Google Maps not loaded yet, will retry...");
        // Retry after a delay, but only if this is still the current calculation
        setTimeout(() => {
          if (currentCalculationRef.current === dayKey) {
            calculateRouteInBackground();
          }
        }, 2000);
        return;
      }

      console.log("🔄 Calculating route in background for", dayKey);
      
      try {
        // Get starting location from settings (startingAddress) or fallback to GPS
        const getStartingLocation = () => {
          return new Promise((resolve) => {
            // First try to use startingAddress from settings
            if (startingAddress && startingAddress.trim()) {
              console.log("📍 Using starting address from settings:", startingAddress);
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ address: startingAddress }, (results, status) => {
                if (status === "OK" && results[0]) {
                  const location = results[0].geometry.location;
                  resolve({
                    lat: location.lat(),
                    lng: location.lng(),
                  });
                } else {
                  console.warn("⚠️ Could not geocode starting address, falling back to GPS");
                  // Fallback to GPS
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        resolve({
                          lat: position.coords.latitude,
                          lng: position.coords.longitude,
                        });
                      },
                      () => {
                        // Default to home base if GPS fails
                        resolve({ lat: 26.3398, lng: -81.7787 });
                      },
                      { timeout: 5000 }
                    );
                  } else {
                    resolve({ lat: 26.3398, lng: -81.7787 });
                  }
                }
              });
            } else {
              // No starting address in settings, use GPS
              console.log("📍 No starting address in settings, using GPS");
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    resolve({
                      lat: position.coords.latitude,
                      lng: position.coords.longitude,
                    });
                  },
                  () => {
                    // Default to home base if GPS fails
                    resolve({ lat: 26.3398, lng: -81.7787 });
                  },
                  { timeout: 5000 }
                );
              } else {
                resolve({ lat: 26.3398, lng: -81.7787 });
              }
            }
          });
        };

        const startLocation = await getStartingLocation();
        
        // Check again if this is still the current calculation (user may have changed dates)
        if (currentCalculationRef.current !== dayKey) {
          console.log("⏭️ Skipping route calculation - date changed to", currentCalculationRef.current);
          return;
        }
        
        const startPoint = new window.google.maps.LatLng(
          startLocation.lat,
          startLocation.lng
        );

        // Use the mapped routeRentalsForHash
        const waypoints = routeRentalsForHash.map((r) => ({
          location: r.deliveryAddress,
          stopover: true,
        }));

        if (waypoints.length === 0) {
          console.log("⚠️ No addresses found for route calculation");
          return;
        }

        // Calculate route using Google Directions API
        const directionsService = new window.google.maps.DirectionsService();
        const request = {
          origin: startPoint,
          destination: waypoints[waypoints.length - 1].location,
          waypoints: waypoints.slice(0, -1),
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING,
        };

        directionsService.route(request, (result, status) => {
          // Only save if this is still the current calculation (prevents race conditions)
          if (currentCalculationRef.current !== dayKey) {
            console.log("⏭️ Ignoring route result - date changed during calculation");
            return;
          }
          
          if (status === "OK") {
            console.log("✅ Route calculated and cached in Zustand store for", dayKey);
            console.log("   Route will be available for RoutePlanner page");
            setCachedRoute(dayKey, result, bookingsHash);
            console.log("   ✅ Route stored in Zustand with hash:", bookingsHash);
          } else {
            console.error("❌ Route calculation failed:", status);
          }
        });
      } catch (error) {
        console.error("❌ Background route calculation error:", error);
      }
    };

    // Start background calculation after a short delay
    const timer = setTimeout(calculateRouteInBackground, 1000);
    return () => {
      clearTimeout(timer);
      // Clear the current calculation ref when effect cleans up
      currentCalculationRef.current = null;
    };
  }, [currentDisplayDate, startingAddress]); // Recalculate when date or starting address changes

  const handleGoToMaps = () => {
    if (selectedRentals.length === 0) {
      alert("Please select at least one customer for route planning");
      return;
    }

    // Get selected rental objects
    const selectedRentalObjects = todaysRentals.filter((rental) =>
      selectedRentals.includes(rental.id)
    );

    console.log(
      "🗺️ Navigating to route planner with",
      selectedRentals.length,
      "customers"
    );
    navigate("/route-planner", { state: { rentals: selectedRentalObjects } });
  };

  const handleGoToRoutePlanner = () => {
    const filteredRentals = getFilteredRentals();
    
    // Filter to only deliveries/pickups with addresses
    // Map to ensure fullName exists for RoutePlanner
    const routeRentals = filteredRentals
      .filter((r) => {
        const hasDelivery = r.delivery || false;
        const hasHomePickup = r.homePickup || false;
        return (hasDelivery || hasHomePickup) && r.deliveryAddress;
      })
      .map((r) => ({
        ...r,
        fullName: r.fullName || `${r.firstName || ""} ${r.lastName || ""}`.trim() || "Unknown",
        customerPhone: r.customerPhone || r.phone,
      }));

    if (routeRentals.length === 0) {
      alert("No deliveries or pickups with addresses found for today");
      return;
    }

    // Get day key and check for cached route
    const dayKey = getTZDayKey(currentDisplayDate.getTime());
    const bookingsHash = generateBookingsHash(routeRentals, startingAddress);
    
    // Get cached route if valid - but only pass serializable data
    const cachedRouteData = isRouteValid(dayKey, bookingsHash) 
      ? getCachedRoute(dayKey) 
      : null;
    
    // Extract only serializable data from cached route
    let serializableCachedRoute = null;
    if (cachedRouteData?.route) {
      try {
        // Only pass the essential route data that can be serialized
        serializableCachedRoute = {
          legs: cachedRouteData.route.legs?.map(leg => ({
            distance: leg.distance,
            duration: leg.duration,
            start_address: leg.start_address,
            end_address: leg.end_address,
            start_location: leg.start_location,
            end_location: leg.end_location,
          })),
          overview_path: cachedRouteData.route.overview_path,
          summary: cachedRouteData.route.summary,
        };
      } catch (e) {
        console.warn("⚠️ Could not serialize cached route, will recalculate");
        serializableCachedRoute = null;
      }
    }

    console.log(
      "🗺️ Navigating to route planner with",
      routeRentals.length,
      "customers",
      serializableCachedRoute ? "(using cached route)" : "(will calculate)"
    );
    
    // Normalize date to avoid timezone issues when passing through navigation
    const normalizedDate = new Date(currentDisplayDate);
    normalizedDate.setHours(0, 0, 0, 0);
    
    navigate("/route-planner", { 
      state: { 
        rentals: routeRentals,
        cachedRoute: serializableCachedRoute,
        selectedDate: normalizedDate, // Pass the normalized current display date
      } 
    });
  };

  // Date navigation handlers
  const handlePreviousDay = () => {
    const newDate = new Date(currentDisplayDate);
    newDate.setDate(newDate.getDate() - 1);
    newDate.setHours(0, 0, 0, 0);
    setCurrentDisplayDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDisplayDate);
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(0, 0, 0, 0);
    setCurrentDisplayDate(newDate);
  };

  // Handle going to today
  const handleGoToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setCurrentDisplayDate(today);
  };

  // Check if current date is today
  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return currentDisplayDate.getTime() === today.getTime();
  };

  // Helper: Check if rental has delivery add-ons
  const hasDeliveryAddOn = (rental) => {
    return (
      rental.addOns &&
      rental.addOns.some(
        (addOn) =>
          addOn.name.toLowerCase().includes("pickup") ||
          addOn.name.toLowerCase().includes("delivery")
      )
    );
  };

  // Helper: Get filtered count for the current tab
  const getFilteredCount = () => {
    const today = new Date().toDateString();
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    return todaysRentals.filter((item) => {
      // Filter by date
      const startMillis = parseFirestoreTimestamp(item.startDate);
      const endMillis = parseFirestoreTimestamp(item.endDate);
      
      if (!startMillis || !endMillis) return false;
      
      const startDate = new Date(startMillis);
      const endDate = new Date(endMillis);
      
      const startIsToday = startDate.toDateString() === today;
      const endIsToday = endDate.toDateString() === today;
      const isActive = startDate <= todayDate && endDate >= todayDate;
      
      return startIsToday || endIsToday || isActive;
    }).length;
  };

  // Helper: Get filtered rentals for the current tab
  const getFilteredRentals = () => {
    // Use currentDisplayDate for filtering
    const targetDate = new Date(currentDisplayDate.getTime());
    
    // Get timezone-aware day key for the target date
    const targetDateKey = getTZDayKey(targetDate.getTime());

    return todaysRentals.filter((item) => {
      // Filter by date using timezone-aware matching (same as Calendar)
      const startMillis = parseFirestoreTimestamp(item.startDate);
      const endMillis = parseFirestoreTimestamp(item.endDate);
      
      if (!startMillis || !endMillis) return false;
      
      const startKey = getTZDayKey(startMillis);
      const endKey = getTZDayKey(endMillis);
      
      // Match if start OR end date matches the current display date
      if (startKey !== targetDateKey && endKey !== targetDateKey) return false;
      
      return true;
    });
  };

  // Helper: Check if there are any pickups or deliveries for route planning
  const hasPickupsOrDeliveries = () => {
    const targetDate = new Date(currentDisplayDate.getTime());
    const targetDateKey = getTZDayKey(targetDate.getTime());

    return todaysRentals.some((item) => {
      const startMillis = parseFirestoreTimestamp(item.startDate);
      const endMillis = parseFirestoreTimestamp(item.endDate);
      
      if (!startMillis || !endMillis) return false;
      
      const startKey = getTZDayKey(startMillis);
      const endKey = getTZDayKey(endMillis);
      
      const hasDelivery = item.delivery || false;
      const hasHomePickup = item.homePickup || false;
      
      // Check if start date matches today AND has delivery
      const hasDeliveryToday = startKey === targetDateKey && hasDelivery;
      // Check if end date matches today AND has home pickup
      const hasPickupToday = endKey === targetDateKey && hasHomePickup;
      
      return hasDeliveryToday || hasPickupToday;
    });
  };

  // Helper: Render the rentals list or empty state
  const renderRentalsList = () => {
    const filteredRentals = getFilteredRentals();

    // Show empty state if no rentals match the current tab filter
    if (filteredRentals.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No rentals scheduled for today</Text>
          <Text style={styles.emptySubtext}>
            Rentals will appear here when received from Peek Pro
          </Text>
        </View>
      );
    }

    // Show the filtered rentals
    return (
      <ScrollView
        style={styles.rentalsScrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.rentalsScrollContent}
      >
        {filteredRentals
          .sort((a, b) => {
            // Sort order: Delivery (1), Pickup (2), In-Store (3)
            const targetKey = getTZDayKey(currentDisplayDate.getTime());
            
            const getRank = (item) => {
              const startMillis = parseFirestoreTimestamp(item.startDate);
              const endMillis = parseFirestoreTimestamp(item.endDate);
              const startKey = getTZDayKey(startMillis);
              const endKey = getTZDayKey(endMillis);
              
              const hasDelivery = item.delivery || false;
              const hasHomePickup = item.homePickup || false;
              
              if (hasDelivery && startKey === targetKey) return 1; // Delivery
              if (hasHomePickup && endKey === targetKey) return 2; // Pickup
              return 3; // In-Store (or others)
            };
            
            return getRank(a) - getRank(b);
          })
          .map((item) => {
            const isSelected = currentCustomer?.phone === item.customerPhone;
            const isChecked = selectedRentals.includes(item.id);

            // Check if this is a delivery (start date is today)
            const startMillis = parseFirestoreTimestamp(item.startDate);
            const endMillis = parseFirestoreTimestamp(item.endDate);
            
            const isDeliveryToday = startMillis ? new Date(startMillis).toDateString() === new Date().toDateString() : false;
            const isPickupToday = endMillis ? new Date(endMillis).toDateString() === new Date().toDateString() : false;

            // Parse tickets into individual items
            let ticketItems = [];
            let duration = "";
            if (item.tickets) {
              const items = item.tickets.split(",").map((t) => t.trim());
              items.forEach((ticketStr) => {
                // Pattern: "1x Sun Drifter (step-thru) - 2 Days"
                const match = ticketStr.match(
                  /(\d+)x\s+([^(-]+)(?:\(([^)]+)\))?\s*-\s*(.+)/i
                );
                if (match) {
                  const quantity = match[1];
                  const bikeName = match[2].trim();
                  const variant = match[3] ? match[3].trim() : null;
                  duration = match[4].trim(); // Capture duration (same for all)

                  ticketItems.push({
                    quantity,
                    name: bikeName,
                    variant,
                  });
                }
              });
            }

            return renderRentalCard(
              item,
              isSelected,
              isChecked,
              isDeliveryToday,
              isPickupToday,
              ticketItems,
              duration
            );
          })}
      </ScrollView>
    );
  };

  // Helper: Render a single rental card
  const renderRentalCard = (
    item,
    isSelected,
    isChecked,
    isDeliveryToday,
    isPickupToday,
    ticketItems,
    duration
  ) => {
    return (
      <View key={item.id} style={styles.rentalCardWrapper}>
        {/* Rest of the rental card JSX will stay in place */}
        {/* This is just a placeholder - the actual JSX is already in the file */}
      </View>
    );
  };

  if (loading || !user) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Loading...</Text>
      </View>
    );
  }

  const currentStyles = isDesktop ? stylesDesktop : stylesMobile;

  return (
    <View style={currentStyles.container}>
      <AuthHeader
        title="Today's Rentals"
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      <View style={isDesktop ? currentStyles.desktopWrapper : null}>
        <ScrollView
          style={currentStyles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Route Planning Button - Full Width */}
        <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              currentStyles.routeButton,
              !hasPickupsOrDeliveries() && currentStyles.routeButtonDisabled
            ]}
            onPress={handleGoToRoutePlanner}
            disabled={!hasPickupsOrDeliveries()}
        >
            <Text style={[
              currentStyles.routeButtonText,
              !hasPickupsOrDeliveries() && currentStyles.routeButtonTextDisabled
            ]}>
              🗺️ Route Planning
            </Text>
        </TouchableOpacity>

          {/* Date Navigation Component */}
          <View style={styles.dateNavigationContainer}>
        <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.dateNavButton}
              onPress={handlePreviousDay}
        >
              <Text style={styles.dateNavButtonText}>
                ◀
              </Text>
            </TouchableOpacity>
            
            <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
              <TouchableOpacity
                onPress={handleGoToToday}
                style={{ alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10 }}
              >
                <Text style={styles.dateDisplayText}>
                  {formatDateShort(currentDisplayDate.getTime())}
                </Text>
              </TouchableOpacity>
            </View>
            
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.dateNavButton}
              onPress={handleNextDay}
            >
              <Text style={styles.dateNavButtonText}>
                ▶
              </Text>
            </TouchableOpacity>
      </View>

      {/* Products Going Out Today - Show/Hide Component */}
      {(() => {
        const filteredRentals = getFilteredRentals();
        const dateKey = getTZDayKey(currentDisplayDate.getTime());
        
        // Get all products going out today (start date matches current display date)
        const productsGoingOutToday = {};
        
        filteredRentals.forEach((rental) => {
          const startMillis = parseFirestoreTimestamp(rental.startDate);
          if (!startMillis) return;
          
          const startKey = getTZDayKey(startMillis);
          if (startKey !== dateKey) return; // Only count products for start date
          
          // Process product1 items
          if (rental.product1 && Array.isArray(rental.product1)) {
            rental.product1.forEach((item) => {
              if (!item || item.quantity <= 0) return;
              
              // Check if product is physical (default to true if not specified)
              const product = products1.find(p => p.id === item.id);
              if (product && product.physicalItem === false) return; // Skip non-physical items
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              const productId = `product1-${item.id}`;
              
              if (!productsGoingOutToday[productId]) {
                productsGoingOutToday[productId] = {
                  name: productName,
                  quantity: 0,
                  type: 'product1'
                };
              }
              productsGoingOutToday[productId].quantity += item.quantity;
            });
          }
          
          // Process product2 items
          if (rental.product2 && Array.isArray(rental.product2)) {
            rental.product2.forEach((item) => {
              if (!item || item.quantity <= 0) return;
              
              // Check if product is physical (default to true if not specified)
              const product = products2.find(p => p.id === item.id);
              if (product && product.physicalItem === false) return; // Skip non-physical items
              
              const productName = item.name || product?.name || `Product ${item.id}`;
              const productId = `product2-${item.id}`;
              
              if (!productsGoingOutToday[productId]) {
                productsGoingOutToday[productId] = {
                  name: productName,
                  quantity: 0,
                  type: 'product2'
                };
              }
              productsGoingOutToday[productId].quantity += item.quantity;
            });
          }
        });
        
        // Separate product1 and product2 items
        const product1List = Object.values(productsGoingOutToday)
          .filter(product => product.type === 'product1')
          .sort((a, b) => a.name.localeCompare(b.name));
        const product2List = Object.values(productsGoingOutToday)
          .filter(product => product.type === 'product2')
          .sort((a, b) => a.name.localeCompare(b.name));
        
        const totalProductsCount = product1List.length + product2List.length;
        if (totalProductsCount === 0) return null;

        // Get product category names from settings
        const product1Name = settings.product1Title?.title || "Product1";
        const product2Name = settings.product2Title?.title || "Product2";

        // Cleanup function for print documents
        const cleanupPrintDocuments = async (printingPath, documentIds) => {
          try {
            for (const docId of documentIds) {
              try {
                const docRef = doc(db, printingPath, docId);
                await deleteDoc(docRef);
                console.log(`✅ Cleaned up print document ${docId}`);
              } catch (error) {
                if (error.code === 'not-found') {
                  console.log(`ℹ️ Document ${docId} already deleted`);
                } else {
                  console.error(`❌ Error deleting document ${docId}:`, error);
                }
              }
            }
          } catch (error) {
            console.error("❌ Error in cleanup:", error);
          }
        };

        // Print products function
        const handlePrintProducts = async () => {
          setPrintingProducts(true);
          try {
            const { tenantId, storeId } = getTenantStoreContext();
            if (!tenantId || !storeId) {
              setPrintingProducts(false);
              alert("Error: Tenant/Store context not available");
              return;
            }

            // Get product category names from settings
            const product1Name = settings.product1Title?.title || "Product1";
            const product2Name = settings.product2Title?.title || "Product2";

            // Format date string to "Mon, Jan 15" format
            const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const dateString = `${dayNames[currentDisplayDate.getDay()]}, ${monthNames[currentDisplayDate.getMonth()]} ${currentDisplayDate.getDate()}`;

            // Create printing collection path
            const printingPath = `tenants/${tenantId}/stores/${storeId}/printing`;
            const printingRef = collection(db, printingPath);

            // Format product1 array (same format as Calendar print)
            const product1Array = [];
            Object.values(productsGoingOutToday).forEach((product) => {
              if (product.type === 'product1' && product.quantity > 0) {
                const formattedProduct = `(${product.quantity}x) ${product.name}`;
                product1Array.push(formattedProduct);
              }
            });

            // Format product2 array (only physical items, same format as Calendar print)
            const product2Array = [];
            Object.entries(productsGoingOutToday).forEach(([productId, product]) => {
              if (product.type === 'product2' && product.quantity > 0) {
                // Extract original item ID from productId (format: "product2-{id}")
                const itemId = productId.replace('product2-', '');
                // Check if product is physical (only include if physicalItem = true)
                const productObj = products2.find(p => p.id === itemId);
                if (productObj && productObj.physicalItem !== false) {
                  const formattedProduct = `(${product.quantity}x) ${product.name}`;
                  product2Array.push(formattedProduct);
                }
              }
            });

            // Create document data (same structure as Calendar print)
            const documentData = {
              firstName: "",
              lastName: "",
              phoneNumber: "",
              dateString: dateString,
              product1: product1Array,
              product2: product2Array,
              product1Name: product1Name,
              product2Name: product2Name,
              delivery: false,
              inStorePickup: false,
              adminNotes: "Products Summary",
            };

            // Add document to collection
            const docRef = await addDoc(printingRef, documentData);
            console.log(`✅ Created products print document ${docRef.id}`);

            // Schedule cleanup after 15 seconds
            setTimeout(() => {
              cleanupPrintDocuments(printingPath, [docRef.id]);
            }, 15000);

            setPrintingProducts(false);
          } catch (error) {
            console.error("❌ Error printing products:", error);
            setPrintingProducts(false);
            alert(`Failed to print products: ${error.message}`);
          }
        };
        
        return (
          <View style={styles.productsGoingOutContainer}>
            <TouchableOpacity
              style={[
                styles.productsGoingOutHeader,
                !showProductsList && styles.productsGoingOutHeaderHidden
              ]}
              onPress={() => setShowProductsList(!showProductsList)}
            >
              <Text style={styles.productsGoingOutTitle}>
                📦 Products Going Out Today ({totalProductsCount})
              </Text>
              <Text style={styles.productsGoingOutToggle}>
                {showProductsList ? '▼' : '▶'}
              </Text>
            </TouchableOpacity>
            
            {showProductsList && (
              <View style={styles.productsGoingOutList}>
                {/* Product1 Section */}
                {product1List.length > 0 && (
                  <>
                    <Text style={[styles.productCategoryHeading, { marginTop: 0 }]}>{product1Name}</Text>
                    {product1List.map((product, index) => (
                      <View key={`product1-${index}`} style={styles.productGoingOutItem}>
                        <Text style={styles.productGoingOutQuantity}>
                          {product.quantity}x
                        </Text>
                        <Text style={styles.productGoingOutName}>
                          {product.name}
                        </Text>
                      </View>
                    ))}
                  </>
                )}
                
                {/* Product2 Section */}
                {product2List.length > 0 && (
                  <>
                    <Text style={styles.productCategoryHeading}>{product2Name}</Text>
                    {product2List.map((product, index) => (
                      <View key={`product2-${index}`} style={styles.productGoingOutItem}>
                        <Text style={styles.productGoingOutQuantity}>
                          {product.quantity}x
                        </Text>
                        <Text style={styles.productGoingOutName}>
                          {product.name}
                        </Text>
                      </View>
                    ))}
                  </>
                )}
                
                {PRINT_PRODUCTS_BUTTON_VISIBLE && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.printProductsButton}
                    onPress={handlePrintProducts}
                  >
                    <Text style={styles.printProductsButtonText}>
                      🖨️ Print Products
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        );
      })()}

      {/* Notification Dialog */}
      <Modal
        visible={showNotificationDialog}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowNotificationDialog(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.notificationDialog}>
            <Text style={styles.dialogTitle}>Notifications</Text>

            <View style={styles.dialogContent}>
              <Text style={styles.dialogStatus}>
                Status:{" "}
                {notificationsEnabled
                  ? notificationsMuted
                    ? "🔕 Muted"
                    : "🔔 Enabled"
                  : "❌ Disabled"}
              </Text>

              {!notificationsEnabled && (
                <Text style={styles.dialogDescription}>
                  Enable notifications to receive updates about new bookings and
                  rentals.
                </Text>
              )}

              {notificationsEnabled && !notificationsMuted && (
                <Text style={styles.dialogDescription}>
                  You're receiving notifications. You can mute them temporarily.
                </Text>
              )}

              {notificationsEnabled && notificationsMuted && (
                <Text style={styles.dialogDescription}>
                  Notifications are muted on this device. Unmute to receive
                  alerts.
                </Text>
              )}
            </View>

            <View style={styles.dialogButtons}>
              {!notificationsEnabled ? (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.dialogButtonPrimary}
                  onPress={() => {
                    setShowNotificationDialog(false);
                    handleNotificationToggle();
                  }}
                >
                  <Text style={styles.dialogButtonPrimaryText}>
                    Enable Notifications
                  </Text>
                </TouchableOpacity>
              ) : notificationsMuted ? (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.dialogButtonPrimary}
                  onPress={() => {
                    setShowNotificationDialog(false);
                    handleNotificationToggle();
                  }}
                >
                  <Text style={styles.dialogButtonPrimaryText}>Unmute</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.dialogButtonSecondary}
                  onPress={() => {
                    setShowNotificationDialog(false);
                    handleNotificationToggle();
                  }}
                >
                  <Text style={styles.dialogButtonSecondaryText}>Mute</Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.dialogButtonCancel}
                onPress={() => setShowNotificationDialog(false)}
              >
                <Text style={styles.dialogButtonCancelText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Printing Products Modal - Blocking */}
      <Modal
        visible={printingProducts}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.printingModal}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.printingModalText}>
              Printing Products...
            </Text>
          </View>
        </View>
      </Modal>

          {/* Go to Route Button Row - Only show if checkboxes selected */}
      {selectedRentals.length > 0 && (
        <View style={styles.routeButtonRow}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.goToRouteButton}
            onPress={handleGoToMaps}
          >
            <Text style={styles.goToRouteButtonText}>
              🗺️ Go to Route ({selectedRentals.length})
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Rentals Content */}
      <View style={styles.rentalsContainer}>
        {(() => {
          // Use getFilteredRentals() which handles selected date from calendar
          const filteredRentals = getFilteredRentals();

          // Show empty state if no rentals
          if (filteredRentals.length === 0) {
            return (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  No rentals scheduled for today
                </Text>
                <Text style={styles.emptySubtext}>
                  Rentals will appear here when received from Peek Pro
                </Text>
              </View>
            );
          }

          // Show the filtered rentals
          return (
            <ScrollView
              style={styles.rentalsScrollView}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.rentalsScrollContent}
            >
              {filteredRentals
                .sort((a, b) => {
                  // Sort by delivery status (deliveries first)
                  const today = new Date().toDateString();
                  const aStartMillis = parseFirestoreTimestamp(a.startDate);
                  const bStartMillis = parseFirestoreTimestamp(b.startDate);
                  const aIsToday = aStartMillis ? new Date(aStartMillis).toDateString() === today : false;
                  const bIsToday = bStartMillis ? new Date(bStartMillis).toDateString() === today : false;

                  if (aIsToday && !bIsToday) return -1;
                  if (!aIsToday && bIsToday) return 1;
                  return 0;
                })
                .map((item) => {
                  const isSelected = currentCustomer?.phone === item.customerPhone;
                  const isChecked = selectedRentals.includes(item.id);

                  // Get start/end millis from Firestore timestamps
                  const startMillis = parseFirestoreTimestamp(item.startDate);
                  const endMillis = parseFirestoreTimestamp(item.endDate);

                  const todayKey = getTZDayKey(Date.now());

                  // Fallback parser for bookingDate strings like "Tuesday, October 21, 2025"
                  const parseBookingDateString = (str) => {
                    if (!str) return null;
                    const dayOfWeekPattern =
                      /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sun|Mon|Tue|Wed|Thu|Fri|Sat),\s*/i;
                    const cleaned = str.replace(dayOfWeekPattern, "");
                    const parsed = new Date(cleaned);
                    return isNaN(parsed.getTime()) ? null : parsed.getTime();
                  };

                  let startKey = null;
                  if (startMillis != null) {
                    startKey = getTZDayKey(startMillis);
                  }

                  let endKey = null;
                  if (endMillis != null) {
                    endKey = getTZDayKey(endMillis);
                  }

                  // If both start and end are today, prefer DELIVERY
                  const isDeliveryToday =
                    startKey != null && startKey === todayKey;
                  const isPickupToday =
                    !isDeliveryToday && endKey != null && endKey === todayKey;

                  // Parse tickets into individual items
                  let ticketItems = [];
                  let duration = "";
                  if (item.tickets) {
                    const items = item.tickets.split(",").map((t) => t.trim());
                    items.forEach((ticketStr) => {
                      // Pattern: "1x Sun Drifter (step-thru) - 2 Days"
                      const match = ticketStr.match(
                        /(\d+)x\s+([^(-]+)(?:\(([^)]+)\))?\s*-\s*(.+)/i
                      );
                      if (match) {
                        const quantity = match[1];
                        const bikeName = match[2].trim();
                        const variant = match[3] ? match[3].trim() : null;
                        duration = match[4].trim(); // Capture duration (same for all)

                        ticketItems.push({
                          quantity,
                          name: bikeName,
                          variant,
                        });
                      }
                    });
                  }
                  // Map rental item to booking structure for Calendar card
                  const targetDate = currentDisplayDate || new Date();
                  const cardInfo = getBookingCardInfo(item, targetDate);
                  
                  // Get product1 and product2 from booking
                  const product1Items = item.product1 || [];
                  const product2Items = item.product2 || [];
                  
                  // Format customer name and phone
                  const customerName = `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.fullName || "Unknown";
                  const capitalizedName = capitalizeWords(customerName);
                  const formattedPhone = formatPhoneNumberCalendar(item.customerPhone || item.phone || "");
                  
                  // Format dates (like Bookings page)
                  const formattedStartDate = formatDateLong(item.startDate);
                  const formattedEndDate = formatDateLong(item.endDate);
                  
                  // Get miles for delivery or pickup
                  const hasDelivery = item.delivery || false;
                  const hasHomePickup = item.homePickup || false;
                  const showMiles = hasDelivery || hasHomePickup;
                  const numMiles = item.numMiles !== undefined && item.numMiles !== null 
                    ? parseFloat(Number(item.numMiles).toFixed(1)) 
                    : null;
                  const milesText = showMiles && numMiles !== null && numMiles > 0 ? `${numMiles} mi` : null;

                  // Format address for display (Street + City, without State and Country)
                  let displayAddress = null;
                  if (item.deliveryAddress) {
                    // If deliveryAddress is a string, parse it to remove state and country
                    const addressString = typeof item.deliveryAddress === 'string' 
                      ? item.deliveryAddress 
                      : JSON.stringify(item.deliveryAddress);
                    
                    // Split by commas and remove state/country (typically last 2 parts)
                    // Format is usually: "Street, City, State, Country" or "Street, City, State"
                    const parts = addressString.split(',').map(p => p.trim());
                    
                    // Remove common country names and state abbreviations from the end
                    const countryPatterns = ['USA', 'United States', 'US'];
                    const lastPart = parts[parts.length - 1];
                    const isCountry = countryPatterns.some(pattern => 
                      lastPart.toLowerCase().includes(pattern.toLowerCase())
                    );
                    
                    // If last part is a country, remove it
                    if (isCountry && parts.length > 2) {
                      parts.pop();
                    }
                    
                    // Remove state (typically 2-letter abbreviation or full state name)
                    // Keep only Street and City (first 2 parts)
                    if (parts.length >= 2) {
                      // Take first part (street) and second part (city)
                      displayAddress = `${parts[0]}, ${parts[1]}`;
                    } else if (parts.length === 1) {
                      displayAddress = parts[0];
                    } else {
                      displayAddress = addressString;
                    }
                  }

                                    return (
                    <TouchableOpacity
                      key={item.id}
                        style={[
                          styles.rentalCard,
                        { borderColor: cardInfo.borderColor, borderWidth: 2 }
                      ]}
                      onPress={() => navigate(`/view-booking/${item.id || item.bookingId}`)}
                    >
                      {/* Badge */}
                      <View
                        style={[
                          styles.rentalBadge,
                          { backgroundColor: cardInfo.color }
                        ]}
                      >
                        <Text style={styles.rentalBadgeText}>
                          {cardInfo.type}
                        </Text>
                      </View>
                      {/* Card Content */}
                      <View style={styles.rentalInfo}>
                        {/* Booking ID */}
                        {item.customerBookingID && (
                          <Text style={styles.bookingIdText}>
                            Booking ID: {item.customerBookingID}
                          </Text>
                        )}
                        {/* Name and Phone on same line */}
                        <View style={styles.namePhoneRow}>
                              <Text style={styles.customerName}>
                            {capitalizedName}
                              </Text>
                          {formattedPhone && (
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                if (isDesktop) {
                                  navigate('/messages', {
                                    state: {
                                      customer: {
                                        id: item.customerPhone || item.phone,
                                        phone: item.customerPhone || item.phone,
                                        fullName: item.fullName || capitalizedName,
                                        firstName: item.firstName || '',
                                        lastName: item.lastName || '',
                                        email: item.customerEmail || item.email || '',
                                        bookingId: item.id || item.bookingId,
                                      }
                                    }
                                  });
                                } else {
                                  const customerObj = {
                                    id: item.customerPhone || item.phone,
                                    firstName: item.firstName,
                                    lastName: item.lastName,
                                    phone: item.customerPhone || item.phone,
                                    email: item.customerEmail || item.email,
                                    fullName: item.fullName || capitalizedName,
                                    photoCount: 0,
                                    createdAt: new Date(),
                                    bookingId: item.id || item.bookingId,
                                  };
                                  setSelectedCustomer(customerObj);
                                  setContactModalVisible(true);
                                }
                              }}
                            >
                              <Text style={[styles.rentalDetails, { color: '#0E7C6B', fontSize: 14 }]}>
                                {formattedPhone}
                              </Text>
                            </TouchableOpacity>
                              )}
                            </View>
                        
                        {/* Email */}
                        {item.customerEmail && (
                          <View style={styles.emailRow}>
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                navigate('/gmail-inbox', {
                                  state: {
                                    customerEmail: item.customerEmail,
                                    composeTo: item.customerEmail,
                                    composeSubject: `${shopInfo?.name || "Bonita Bikes"} rental communication`,
                                    customerName: capitalizedName,
                                    storeName: shopInfo?.name || "Bonita Bikes"
                                  }
                                });
                              }}
                            >
                              <Text style={[styles.rentalDetails, { color: '#0E7C6B', textDecorationLine: 'underline', fontSize: 14 }]}>
                                {item.customerEmail}
                              </Text>
                            </TouchableOpacity>
                          </View>
                        )}
                        
                        {/* Address Link - Right side, under phone */}
                        {displayAddress && (
                          <View style={styles.addressRow}>
                            <TouchableOpacity 
                              style={styles.addressLinkContainer}
                              onPress={(e) => {
                                e.stopPropagation();
                                navigate("/map-viewer", { 
                                  state: { 
                                    address: item.deliveryAddress,
                                    customerName: item.fullName || capitalizedName
                                  } 
                                });
                              }}
                            >
                              <Text style={styles.addressLinkText}>📍 {displayAddress}</Text>
                            </TouchableOpacity>
                          </View>
                        )}

                        {/* Start Date */}
                        <View style={styles.dateRow}>
                          <Text style={styles.dateLabel}>Start:</Text>
                          <Text style={[styles.rentalDetails, { color: "#1F8A5B", fontWeight: "500" }]}>
                            {formattedStartDate}
                          </Text>
                        </View>
                        
                        {/* End Date */}
                        <View style={styles.dateRow}>
                          <Text style={styles.dateLabel}>End:</Text>
                          <Text style={[styles.rentalDetails, { color: "#C4453D", fontWeight: "500" }]}>
                            {formattedEndDate}
                          </Text>
                        </View>
                        
                        {/* Miles (if applicable) */}
                        {milesText && (
                          <View style={styles.dateRow}>
                            <Text style={styles.dateLabel}>Distance:</Text>
                            <Text style={styles.rentalDetails}>
                              {milesText}
                            </Text>
                          </View>
                        )}
                        {/* Product items container - Centered */}
                        <View style={styles.productsContainer}>
                          {/* Product1 items - Blue */}
                          {product1Items.map((product, idx) => (
                            <Text key={`p1-${idx}`} style={[styles.rentalDetails, styles.product1Item]}>
                              {product.quantity || 0}x {product.name || "Unknown"}
                                </Text>
                              ))}
                          {/* Product2 items - Green */}
                          {product2Items.map((product, idx) => (
                            <Text key={`p2-${idx}`} style={[styles.rentalDetails, styles.product2Item]}>
                              {product.quantity || 0}x {product.name || "Unknown"}
                                      </Text>
                          ))}
                        </View>
                        
                        {/* Assigned Locks List */}
                        <View style={styles.locksSection}>
                          {(!item.assignedLocks || item.assignedLocks.length === 0) ? (
                            <Text style={styles.noLocksAssignedText}>No locks assigned</Text>
                          ) : (
                            <View style={styles.locksListContainer}>
                              {item.assignedLocks
                                .map(lock => {
                                  // Handle both object format {name, tagNumber, combination} and legacy number format
                                  if (typeof lock === 'object' && lock !== null) {
                                    return {
                                      tagNumber: lock.tagNumber || lock.name || null,
                                      combination: lock.combination || lock.combo || getCombinationFromCache(lock.tagNumber || lock.name) || "",
                                      name: lock.name || lock.tagNumber?.toString() || ""
                                    };
                                  } else {
                                    // Legacy format: just a number (tag number)
                                    const tagNum = typeof lock === 'number' ? lock : parseInt(lock);
                                    return {
                                      tagNumber: tagNum,
                                      combination: getCombinationFromCache(tagNum) || "",
                                      name: tagNum.toString()
                                    };
                                  }
                                })
                                .filter(lock => lock.tagNumber !== null)
                                .sort((a, b) => (a.tagNumber || 0) - (b.tagNumber || 0))
                                .map((lock, idx) => (
                                  <Text key={`lock-${idx}`} style={styles.lockItemText}>
                                    🏷️ #{lock.tagNumber || lock.name} {lock.combination ? `• 🔢 ${lock.combination}` : ""}
                                  </Text>
                                ))}
                            </View>
                          )}
                        </View>
                      </View>

                      {/* Waiver Buttons - Show only if waiver is not signed */}
                      {!item.waiverSigned && (
                        <View style={styles.waiverButtonsRow}>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[styles.signWaiverButton, styles.waiverButtonHalf]}
                            onPress={(e) => {
                              e.stopPropagation();
                              // Check if has bicycles (product1 items)
                              const hasBicycles = product1Items && product1Items.length > 0 && product1Items.some(p => (p.quantity || 0) > 0);
                              navigate("/waiver", {
                                state: {
                                  customerName: capitalizedName,
                                  hasBicycles: hasBicycles,
                                  bookingId: item.id || item.bookingId,
                                  isOriginalBooker: true,
                                },
                              });
                            }}
                          >
                            <Text style={styles.signWaiverButtonText}>📝 Sign Waiver</Text>
                          </TouchableOpacity>
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[styles.sendWaiverButton, styles.waiverButtonHalf]}
                            onPress={async (e) => {
                              e.stopPropagation();
                              try {
                                const { tenantId, storeId } = getTenantStoreContext();
                                const sendWaiverLink = httpsCallable(functions, "sendWaiverLinkSMS");
                                await sendWaiverLink({
                                  bookingId: item.id || item.bookingId,
                                  tenantId,
                                  storeId,
                                });
                                alert("✅ Waiver link sent successfully!");
                              } catch (error) {
                                console.error("❌ Error sending waiver link:", error);
                                alert("Error: Failed to send waiver link - " + error.message);
                              }
                            }}
                          >
                            <Text style={styles.sendWaiverButtonText}>📱 Send Waiver</Text>
                          </TouchableOpacity>
                        </View>
                      )}

                      {/* Payment Collection Buttons - Only show if no payments */}
                      {(() => {
                        // Calculate payment totals for this booking
                        let totalPaid = 0;
                        let hasPaymentData = false;
                        const paymentsArray = item.payments;
                        if (paymentsArray && Array.isArray(paymentsArray) && paymentsArray.length > 0) {
                          paymentsArray.forEach(payment => {
                            const paymentAmount = payment.costBreakdown?.totalCharged || payment.amount || payment.total || payment.totalCharge || 0;
                            totalPaid += paymentAmount;
                          });
                          hasPaymentData = totalPaid > 0;
                        }

                        if (hasPaymentData) return null;

                        return (
                          <View style={styles.paymentButtonsRow}>
                            <TouchableOpacity dataSet={{ hover: "lift" }}
                              style={[styles.paymentButton, styles.collectPaymentButton]}
                              onPress={(e) => {
                                e.stopPropagation();
                                handleCollectPayment(item);
                              }}
                              disabled={collectingPayment || sendingInvoice}
                            >
                              {collectingPayment ? (
                                <ActivityIndicator color="#FFFFFF" size="small" />
                              ) : (
                                <Text style={styles.buttonText}>💳 Collect Payment</Text>
                              )}
                            </TouchableOpacity>
                            <TouchableOpacity dataSet={{ hover: "lift" }}
                              style={[styles.paymentButton, styles.sendInvoiceButton]}
                              onPress={(e) => {
                                e.stopPropagation();
                                handleSendTextInvoice(item);
                              }}
                              disabled={collectingPayment || sendingInvoice}
                            >
                              {sendingInvoice ? (
                                <ActivityIndicator color="#FFFFFF" size="small" />
                              ) : (
                                <Text style={styles.buttonText}>📱 Send Text Invoice</Text>
                              )}
                            </TouchableOpacity>
                          </View>
                        );
                      })()}

                      {/* Admin notes - above button row */}
                      {item.adminNotes?.trim() && (
                        <View style={styles.adminNotesCard}>
                          <Text style={styles.adminNotesLabel}>Admin  Notes:</Text>
                          <Text style={styles.adminNotesText}>{item.adminNotes.trim()}</Text>
                        </View>
                      )}

                      {/* Divider Line */}
                      <View style={styles.actionButtonsDivider} />

                      {/* Action Buttons Row */}
                      <View style={styles.actionButtonsRow}>
                        {/* Lock Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => {
                            console.log(
                              "🔒 Opening lock assignment for:",
                              item.fullName
                            );
                            navigate("/customer-locks", {
                              state: { rental: item },
                            });
                          }}
                        >
                          <Text style={styles.iconButtonText}>🔒</Text>
                        </TouchableOpacity>

                        {/* Photo Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => {
                            console.log(
                              "📸 Opening photo page for:",
                              item.fullName
                            );

                            // Prepare customer object
                            const customerObj = {
                              id: item.customerPhone,
                              firstName: item.firstName,
                              lastName: item.lastName,
                              phone: item.customerPhone,
                              fullName: item.fullName,
                              photoCount: 0,
                              createdAt: new Date(),
                              bookingId: item.id || item.bookingId, // Add bookingId for media tracking
                            };

                            // Set as current customer
                            setCustomer(customerObj);
                            console.log(
                              "✅ Set rental customer as current customer"
                            );

                            // Navigate to photo page with category
                            navigate("/photo", {
                              state: {
                                category: "outbound",
                                bookingId: item.id || item.bookingId,
                                fromToday: true,
                              },
                            });
                          }}
                        >
                          <Text style={styles.iconButtonText}>📸</Text>
                        </TouchableOpacity>
                      </View>
                    </TouchableOpacity>
                  );
                })}
            </ScrollView>
          );
        })()}
          </View>
        </ScrollView>
      </View>

      {/* Floating Upload Counter */}
      {(uploadsCompleted > 0 || uploadsInProgress > 0) && (
        <View style={styles.uploadCounter}>
          <Text style={styles.uploadCounterNumber}>{uploadsCompleted}</Text>
          <Text style={styles.uploadCounterLabel}>Uploaded</Text>
          {uploadsInProgress > 0 && (
            <View style={styles.uploadPendingIndicator}>
              <Text style={styles.uploadPendingText}>
                {uploadsInProgress} uploading...
              </Text>
              <View style={styles.uploadSpinner}>
                <ActivityIndicator size="small" color="#FFFFFF" />
              </View>
            </View>
          )}
        </View>
      )}

      {/* Contact Options Modal */}
      <ContactOptionsModal
        visible={contactModalVisible}
        onClose={() => setContactModalVisible(false)}
        customer={selectedCustomer}
      />
    </View>
  );
};

// Desktop Styles
const stylesDesktop = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  scrollView: {
    flex: 1,
  },
  calendarButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginTop: 10,
    marginBottom: 5,
    marginHorizontal: 12,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  calendarButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  routeButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginTop: 5,
    marginBottom: 10,
    marginHorizontal: 12,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  routeButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  routeButtonDisabled: {
    backgroundColor: "#8A9794",
    opacity: 1,
  },
  routeButtonTextDisabled: {
    color: "#FFFFFF",
    textAlign: "center",
  },
});

// Mobile Styles
const stylesMobile = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },
  desktopWrapper: {
    // Not used on mobile
  },
  scrollView: {
    flex: 1,
  },
  calendarButton: {
    alignSelf: "stretch",
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginTop: 10,
    marginBottom: 10,
    marginHorizontal: 12,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  calendarButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  routeButton: {
    alignSelf: "stretch",
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginTop: 5,
    marginBottom: 10,
    marginHorizontal: 12,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  routeButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  routeButtonDisabled: {
    backgroundColor: "#8A9794",
    opacity: 1,
  },
  routeButtonTextDisabled: {
    color: "#FFFFFF",
    textAlign: "center",
  },
});

const styles = StyleSheet.create({
  hamburgerButton: {
    width: 44,
    height: 44,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  hamburgerIcon: {
    fontSize: 24,
    color: "#FFFFFF",
  },
  dropdownMenu: {
    position: "absolute",
    top: 80,
    left: 16,
    width: 250,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 8,
    zIndex: 1000,
  },
  menuItem: {
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  menuItemText: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
  },
  routeButtonRow: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: "#F4F6F6",
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  goToRouteButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  goToRouteButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  iconButton: {
    width: 44,
    height: 44,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonActive: {
    backgroundColor: "#1F8A5B",
  },
  iconButtonText: {
    fontSize: 24,
    textAlign: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  notificationDialog: {
    width: "90%",
    maxWidth: 400,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
  },
  printingModal: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 200,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
  },
  printingModalText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  dialogTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
    textAlign: "center",
  },
  dialogContent: {
    marginBottom: 24,
  },
  dialogStatus: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
  },
  dialogDescription: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
  },
  dialogButtons: {
  },
  dialogButtonPrimary: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
  },
  dialogButtonPrimaryText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  dialogButtonSecondary: {
    backgroundColor: "#B4761B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
  },
  dialogButtonSecondaryText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  dialogButtonCancel: {
    backgroundColor: "#EDF1F0",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
  },
  dialogButtonCancelText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    padding: 16,
    marginVertical: 16,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 4,
  },
  formTitle: {
    fontSize: 19,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 6,
  },
  required: {
    color: "#C4453D",
    fontSize: 13,
    fontWeight: "bold",
  },
  input: {
    height: 45,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    paddingHorizontal: 12,
    fontSize: 15,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
  },
  helperText: {
    fontSize: 11,
    color: "#8A9794",
    marginTop: 4,
  },
  submitButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 13,
    borderRadius: 5,
    alignItems: "center",
    marginTop: 8,
  },
  submitButtonDisabled: {
    backgroundColor: "#1F8A5B",
  },
  successMessage: {
    backgroundColor: "#E4F4EC",
    padding: 10,
    borderRadius: 5,
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: "#1F8A5B",
  },
  successText: {
    color: "#1F8A5B",
    fontSize: 13,
    fontWeight: "600",
  },
  searchInfo: {
    backgroundColor: "#FDF3E3",
    padding: 8,
    borderRadius: 5,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: "#B4761B",
  },
  searchInfoText: {
    color: "#7A4E12",
    fontSize: 12,
    fontWeight: "600",
  },
  resultsContainer: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    padding: 16,
    marginVertical: 16,
    maxHeight: 350,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 4,
  },
  resultsTitle: {
    fontSize: 17,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  flatList: {
    maxHeight: 250,
  },
  customerItemWrapper: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  customerItem: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F4F6F6",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  rentalCardWrapper: {
    marginBottom: 12,
  },
  deliveryBadge: {
    backgroundColor: "#0E7C6B", // Blue - matches calendar DELIVERY color
    paddingVertical: 6,
    paddingHorizontal: 0,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  deliveryBadgeHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 30,
  },
  deliveryCheckboxContainer: {
    width: 50,
    justifyContent: "center",
    alignItems: "center",
  },
  deliveryBadgeTextContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  deliveryBadgeRightSpacer: {
    width: 50,
  },
  deliveryCheckbox: {
    width: 17,
    height: 17,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: "#FFFFFF",
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  deliveryCheckboxChecked: {
    backgroundColor: "#FFFFFF",
  },
  deliveryCheckboxCheck: {
    color: "#1F8A5B",
    fontSize: 14,
    fontWeight: "bold",
    lineHeight: 14,
  },
  deliveryBadgeText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    letterSpacing: 1,
    textAlign: "center",
  },
  deliveryItemText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "500",
    marginTop: 4,
    opacity: 0.95,
    textAlign: "center",
  },
  pickupBadge: {
    backgroundColor: "#1F8A5B", // Green - matches calendar PICKUP color
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupBadgeText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    letterSpacing: 1,
  },
  pickupItemText: {
    color: "#0F1A19",
    fontSize: 11,
    fontWeight: "500",
    marginTop: 4,
    opacity: 0.85,
    textAlign: "center",
  },
  rentalCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 10,
  },
  rentalBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  rentalBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  rentalInfo: {
    padding: 10,
  },
  bookingIdText: {
    fontSize: 11,
    color: "#5C6B68",
    fontWeight: "600",
    marginBottom: 5,
  },
  namePhoneRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },
  emailRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 5,
  },
  durationMilesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },
  dateRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "600",
    marginRight: 8,
    minWidth: 50,
  },
  addressRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 5,
  },
  addressLinkContainer: {
    alignSelf: 'center',
  },
  addressLinkText: {
    fontSize: 15,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  rentalDetails: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 3,
  },
  product1Item: {
    color: "#0E7C6B", // Blue
    fontSize: 13,
  },
  product2Item: {
    color: "#1F8A5B", // Green
    fontSize: 13,
  },
  productsContainer: {
    alignItems: "center",
    marginTop: 3,
  },
  locksSection: {
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    padding: 10,
    marginTop: 10,
    marginHorizontal: 8,
    borderLeftWidth: 3,
    borderLeftColor: "#B4761B",
  },
  locksListContainer: {
    alignItems: "center",
    gap: 4,
  },
  lockItemText: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "500",
  },
  noLocksAssignedText: {
    fontSize: 14,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
  },
  rentalCardWithBadge: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  rentalCardNoBadge: {
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  ticketsSection: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  ticketItem: {
    fontSize: 13,
    color: "#2E3A38",
    marginBottom: 4,
    fontWeight: "500",
  },
  addOnsSection: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  addOnItem: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 4,
    fontStyle: "italic",
  },
  addOnSubItem: {
    marginLeft: 20,
    fontSize: 12,
  },
  ticketDuration: {
    fontSize: 12,
    color: "#0E7C6B",
    marginTop: 4,
    fontWeight: "600",
  },
  rentalPeriod: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
  },
  rentalPeriodText: {
    fontSize: 12,
    color: "#2E3A38",
    marginBottom: 4,
    fontWeight: "500",
  },
  horizontalSpacer: {
    height: 1,
    backgroundColor: "#DDE3E2",
    marginHorizontal: 14,
  },
  adminNotesCard: {
    marginTop: 12,
    marginHorizontal: 12,
    marginBottom: 8,
    padding: 10,
    backgroundColor: "#FDF3E3",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#F4F6F6",
    borderLeftWidth: 3,
    borderLeftColor: "#B4761B",
  },
  adminNotesLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  adminNotesText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 18,
  },
  actionButtonsDivider: {
    height: 1,
    backgroundColor: "#DDE3E2",
    marginVertical: 8,
    marginHorizontal: 12,
  },
  actionButtonsRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F4F6F6",
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderTopWidth: 0,
  },
  checkboxButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    marginLeft: 10,
    borderRadius: 4,
    minHeight: 44,
  },
  checkboxBox: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  checkboxBoxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxCheck: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "600",
  },
  messageButton: {
    backgroundColor: "#0E7C6B",
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  messageButtonSmall: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#0E7C6B",
    borderRadius: 5,
  },
  messageButtonIcon: {
    fontSize: 19,
    color: "#FFFFFF",
  },
  messageButtonLabel: {
    fontSize: 15,
    color: "#FFFFFF",
    fontWeight: "600",
    textAlign: "center",
  },
  iconButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 0,
    paddingHorizontal: 12,
    borderRadius: 5,
    minHeight: 44,
    height: 44,
  },
  iconButtonText: {
    fontSize: 29,
    lineHeight: 29,
    textAlign: "center",
    marginTop: -10,
  },
  goToMapsButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  customerInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  nameLeftGroup: {
    flexDirection: "row",
    alignItems: "center",
  },
  customerName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  bookingSource: {
    fontSize: 17,
  },
  phoneClickable: {
    alignSelf: "flex-start",
  },
  phoneAddressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginTop: 5,
  },
  customerPhone: {
    fontSize: 16,
    color: "#0E7C6B",
  },
  partySize: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  customerAddress: {
    fontSize: 13,
    color: "#0E7C6B",
    marginTop: 2,
  },
  deliveryNotes: {
    fontSize: 13,
    color: "#B4761B",
    marginTop: 2,
    fontStyle: "italic",
    textAlign: "right",
  },
  assignedLocksContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 8,
    backgroundColor: "#EDF1F0",
    borderRadius: 6,
    padding: 8,
  },
  assignedLocksLabel: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "600",
  },
  horizontalDivider: {
    height: 1,
    backgroundColor: "#DDE3E2",
    marginTop: 8,
  },
  lockTagButton: {
    paddingHorizontal: 2,
  },
  lockTagText: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  assignedLocks: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 2,
    fontWeight: "600",
  },
  customerTickets: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 2,
    fontStyle: "italic",
  },
  customerTotal: {
    fontSize: 13,
    color: "#1F8A5B",
    marginTop: 2,
    fontWeight: "600",
  },
  selectText: {
    color: "#1F8A5B",
    fontSize: 24,
    fontWeight: "bold",
  },
  emptyContainer: {
    flex: 1,
    paddingVertical: 30,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  button: {
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 24,
    paddingVertical: 13,
    borderRadius: 6,
    marginTop: 8,
    marginBottom: 16,
    width: "100%",
    maxWidth: 500,
    alignItems: "center",
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  tabContainer: {
    flexDirection: "row",
    width: "100%",
    marginBottom: 10,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#DDE3E2",
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: "center",
    backgroundColor: "#F6F8F7",
  },
  tabActive: {
    backgroundColor: "#0E7C6B",
  },
  tabText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#5C6B68",
  },
  tabTextActive: {
    color: "#FFFFFF",
  },
  rentalsContainer: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 12,
    paddingTop: 40,
    marginBottom: 0,
  },
  rentalsTitle: {
    fontSize: 17,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  rentalsScrollView: {
    flex: 1,
    width: "100%",
  },
  rentalsScrollContent: {
    paddingBottom: 20,
    flexGrow: 1,
    justifyContent: "center",
  },
  dateNavigationContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    marginHorizontal: 12,
    marginTop: 10,
    marginBottom: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  dateNavButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
  },
  dateNavButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  dateNavButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  dateNavButtonTextDisabled: {
    color: "#8A9794",
    textAlign: "center",
  },
  dateDisplay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  dateDisplayText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  rentalPeriodRow: {
    marginBottom: 4,
  },
  rentalPeriodLabel: {
    fontSize: 13,
    fontWeight: "600",
  },
  deliveryLabel: {
    color: "#1F8A5B", // Green for delivery
  },
  pickupLabel: {
    color: "#0E7C6B", // Blue for pickup
  },
  defaultLabel: {
    color: "#2E3A38", // Default color
  },
  bookingTime: {
    fontSize: 13,
    color: "#0E7C6B",
    marginTop: 2,
    fontWeight: "600",
  },
  emptySubtext: {
    fontSize: 12,
    color: "#8A9794",
    marginTop: 6,
    textAlign: "center",
  },
  uploadCounter: {
    position: "fixed",
    bottom: 20,
    right: 20,
    backgroundColor: "#0E7C6B",
    borderRadius: 12,
    padding: 16,
    minWidth: 120,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    zIndex: 1000,
  },
  uploadCounterNumber: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#FFFFFF",
    lineHeight: 40,
  },
  uploadCounterLabel: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 4,
    marginBottom: 8,
  },
  uploadPendingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 4,
  },
  uploadPendingText: {
    fontSize: 12,
    color: "#FFFFFF",
    opacity: 0.9,
  },
  uploadSpinner: {
    marginLeft: 4,
  },
  productsGoingOutContainer: {
    backgroundColor: '#E3F2EF',
    marginHorizontal: 12,
    marginBottom: 10,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#0E7C6B',
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 4,
    overflow: 'hidden',
  },
  productsGoingOutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#E3F2EF',
    borderBottomWidth: 1,
    borderBottomColor: '#0E7C6B',
  },
  productsGoingOutHeaderHidden: {
    backgroundColor: '#FDF3E3',
    borderBottomWidth: 0,
    borderBottomColor: 'transparent',
  },
  productsGoingOutTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0E7C6B',
  },
  productsGoingOutToggle: {
    fontSize: 16,
    color: '#0E7C6B',
    fontWeight: 'bold',
  },
  productsGoingOutList: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#E3F2EF',
  },
  productCategoryHeading: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginTop: 12,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#0E7C6B',
  },
  productGoingOutItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EDF1F0',
  },
  printProductsButton: {
    marginTop: 12,
    backgroundColor: '#1F8A5B',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  printProductsButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  productGoingOutQuantity: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#0E7C6B',
    minWidth: 45,
  },
  productGoingOutName: {
    fontSize: 15,
    color: '#2E3A38',
    flex: 1,
  },
  waiverButtonsRow: {
    flexDirection: 'row',
    gap: 12,
    marginHorizontal: 12,
    marginTop: 8,
    marginBottom: 8,
  },
  waiverButtonHalf: {
    flex: 1,
  },
  signWaiverButton: {
    backgroundColor: '#B4761B', // Bright yellow
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  signWaiverButtonText: {
    color: '#0F1A19',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  sendWaiverButton: {
    backgroundColor: '#1F8A5B', // Green
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sendWaiverButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  paymentButtonsRow: {
    flexDirection: "row",
    marginTop: 8,
    marginBottom: 8,
    paddingHorizontal: 12,
    gap: 12,
    width: "100%",
  },
  paymentButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  collectPaymentButton: {
    backgroundColor: "#C4453D", // Red
  },
  sendInvoiceButton: {
    backgroundColor: "#0E7C6B", // Magenta
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default Base;
